import { useState, useEffect } from 'react';

export function usePersonalization() {
  const [history, setHistory] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [segment, setSegment] = useState('general');
  const [visitorType, setVisitorType] = useState('new');

  useEffect(() => {
    // Load history and interactions from localStorage
    const stored = localStorage.getItem('user_history');
    const storedInteractions = localStorage.getItem('user_interactions');
    const firstVisit = localStorage.getItem('first_visit');
    
    if (stored) {
      const parsed = JSON.parse(stored);
      setHistory(parsed);
      
      const interactions = storedInteractions ? JSON.parse(storedInteractions) : [];
      setInteractions(interactions);
      
      setSegment(determineSegment(parsed, interactions));
    }
    
    // Track visitor type
    if (!firstVisit) {
      localStorage.setItem('first_visit', new Date().toISOString());
      setVisitorType('new');
    } else {
      const daysSinceFirst = (new Date() - new Date(firstVisit)) / (1000 * 60 * 60 * 24);
      if (daysSinceFirst > 30) {
        setVisitorType('returning-long');
      } else if (daysSinceFirst > 7) {
        setVisitorType('returning');
      } else {
        setVisitorType('active');
      }
    }
  }, []);

  const trackPageView = (pageTitle, pageUrl, category, keywords = []) => {
    const view = {
      title: pageTitle,
      url: pageUrl,
      category,
      keywords,
      timestamp: new Date().toISOString()
    };

    const newHistory = [view, ...history.filter(h => h.url !== pageUrl).slice(0, 49)];
    setHistory(newHistory);
    localStorage.setItem('user_history', JSON.stringify(newHistory));
    setSegment(determineSegment(newHistory, interactions));
  };

  const trackInteraction = (type, details = {}) => {
    const interaction = {
      type, // 'cta_click', 'form_submit', 'phone_click', 'booking_click', 'video_watch'
      details,
      timestamp: new Date().toISOString()
    };

    const newInteractions = [interaction, ...interactions.slice(0, 99)];
    setInteractions(newInteractions);
    localStorage.setItem('user_interactions', JSON.stringify(newInteractions));
    setSegment(determineSegment(history, newInteractions));
  };

  const determineSegment = (viewHistory, interactionHistory = []) => {
    if (!viewHistory || viewHistory.length === 0) return 'general';

    // Count page views by category
    const implantViews = viewHistory.filter(v => 
      v.url?.includes('implant') || 
      v.keywords?.some(k => k.toLowerCase().includes('implant'))
    ).length;

    const cosmeticViews = viewHistory.filter(v => 
      v.url?.includes('cosmetic') || 
      v.keywords?.some(k => ['veneers', 'whitening', 'smile makeover', 'cosmetic'].some(term => k.toLowerCase().includes(term)))
    ).length;

    const generalViews = viewHistory.filter(v => 
      v.url?.includes('general') || 
      v.keywords?.some(k => ['cleaning', 'checkup', 'preventive', 'exam'].some(term => k.toLowerCase().includes(term)))
    ).length;

    const emergencyViews = viewHistory.filter(v => 
      v.url?.includes('emergency') || 
      v.keywords?.some(k => ['emergency', 'pain', 'urgent', 'broken'].some(term => k.toLowerCase().includes(term)))
    ).length;

    const financeViews = viewHistory.filter(v => 
      v.url?.includes('financing') || 
      v.keywords?.some(k => ['financing', 'payment', 'cost', 'insurance'].some(term => k.toLowerCase().includes(term)))
    ).length;

    // Count interactions by type and context
    const bookingClicks = interactionHistory.filter(i => i.type === 'booking_click').length;
    const phoneClicks = interactionHistory.filter(i => i.type === 'phone_click').length;
    const formSubmits = interactionHistory.filter(i => i.type === 'form_submit').length;
    const implantCTAs = interactionHistory.filter(i => 
      i.type === 'cta_click' && i.details?.context?.includes('implant')
    ).length;
    const cosmeticCTAs = interactionHistory.filter(i => 
      i.type === 'cta_click' && i.details?.context?.includes('cosmetic')
    ).length;

    // Calculate engagement score
    const engagementScore = bookingClicks * 10 + phoneClicks * 8 + formSubmits * 15 + 
                           (implantCTAs + cosmeticCTAs) * 5;

    // High intent users (strong signals from interactions)
    if (engagementScore >= 30) {
      if (implantViews > cosmeticViews && (implantCTAs > 0 || bookingClicks > 0)) {
        return 'high-intent-implant';
      }
      if (cosmeticViews > implantViews && (cosmeticCTAs > 0 || bookingClicks > 0)) {
        return 'high-intent-cosmetic';
      }
      return 'high-intent-general';
    }

    // Emergency takes priority
    if (emergencyViews >= 2 || phoneClicks >= 2) return 'emergency';

    // Granular implant vs cosmetic segmentation
    const totalRelevantViews = implantViews + cosmeticViews;
    if (totalRelevantViews >= 3) {
      const implantRatio = implantViews / totalRelevantViews;
      const cosmeticRatio = cosmeticViews / totalRelevantViews;

      if (implantRatio > 0.7) return 'implant-focused';
      if (cosmeticRatio > 0.7) return 'cosmetic-focused';
      if (implantRatio > 0.4 && cosmeticRatio > 0.4) return 'full-transformation-seeker';
      if (implantViews >= 2) return 'implant-exploring';
      if (cosmeticViews >= 2) return 'cosmetic-exploring';
    }

    // Single category interest
    if (implantViews >= 2 && cosmeticViews === 0) return 'implant-only';
    if (cosmeticViews >= 2 && implantViews === 0) return 'cosmetic-only';

    // Other segments
    if (financeViews >= 2) return 'finance-conscious';
    if (generalViews >= 2) return 'preventive-care';
    if (viewHistory.length >= 5) return 'active-researcher';
    if (viewHistory.length >= 2) return 'interested-visitor';
    
    return 'general';
  };

  const getRecommendations = () => {
    const allContent = [
      { title: 'Understanding Dental Implants', url: '/dental-implants', category: 'implants', keywords: ['implants', 'tooth replacement'], score: 0 },
      { title: 'Single Tooth Replacement', url: '/single-implant', category: 'implants', keywords: ['single tooth', 'implant'], score: 0 },
      { title: 'Full Mouth Restoration', url: '/full-mouth-restoration', category: 'implants', keywords: ['full mouth', 'all on 4'], score: 0 },
      { title: 'Fixed New Teeth Solution', url: '/fixed-new-teeth', category: 'implants', keywords: ['fixed teeth', 'permanent'], score: 0 },
      { title: 'Cosmetic Dentistry Services', url: '/cosmetic-dentistry', category: 'cosmetic', keywords: ['cosmetic', 'smile makeover'], score: 0 },
      { title: 'Invisalign Clear Aligners', url: '/invisalign', category: 'cosmetic', keywords: ['invisalign', 'aligners', 'straighten'], score: 0 },
      { title: 'Same-Day Crowns', url: '/same-day-crowns', category: 'general', keywords: ['crowns', 'cerec', 'same day'], score: 0 },
      { title: 'Emergency Dental Care', url: '/emergency-dentistry', category: 'emergency', keywords: ['emergency', 'urgent', 'pain'], score: 0 },
      { title: 'Financing Your Treatment', url: '/financing-options', category: 'info', keywords: ['financing', 'payment plans', 'cost'], score: 0 },
      { title: 'Meet Dr. Gjelaj', url: '/meet-the-doctor', category: 'info', keywords: ['doctor', 'credentials'], score: 0 },
      { title: 'Dental Implants FAQ', url: '/dental-implants-faq', category: 'info', keywords: ['faq', 'questions', 'implants'], score: 0 },
      { title: 'Our Technology', url: '/our-technology', category: 'info', keywords: ['technology', 'advanced', 'equipment'], score: 0 }
    ];

    const visitedUrls = history.map(h => h.url);
    const userKeywords = history.flatMap(h => h.keywords || []);
    
    // Interaction context scoring
    const implantInteractions = interactions.filter(i => 
      i.details?.context?.includes('implant')
    ).length;
    const cosmeticInteractions = interactions.filter(i => 
      i.details?.context?.includes('cosmetic')
    ).length;

    allContent.forEach(content => {
      if (visitedUrls.includes(content.url)) {
        content.score = -100;
        return;
      }

      // Keyword matching (base score)
      const keywordMatches = content.keywords.filter(k => 
        userKeywords.some(uk => uk.toLowerCase().includes(k.toLowerCase()) || k.toLowerCase().includes(uk.toLowerCase()))
      ).length;
      content.score += keywordMatches * 10;

      // Interaction-based boosting (strongest signal)
      if (content.category === 'implants' && implantInteractions > 0) content.score += 30;
      if (content.category === 'cosmetic' && cosmeticInteractions > 0) content.score += 30;

      // Granular segment boosting
      const segmentBoosts = {
        'high-intent-implant': { implants: 40, info: 10 },
        'high-intent-cosmetic': { cosmetic: 40, info: 10 },
        'implant-focused': { implants: 35 },
        'cosmetic-focused': { cosmetic: 35 },
        'implant-only': { implants: 30 },
        'cosmetic-only': { cosmetic: 30 },
        'full-transformation-seeker': { implants: 25, cosmetic: 25 },
        'implant-exploring': { implants: 20, info: 15 },
        'cosmetic-exploring': { cosmetic: 20, info: 15 },
        'emergency': { emergency: 50 },
        'finance-conscious': { info: 30 }
      };

      const boost = segmentBoosts[segment];
      if (boost && boost[content.category]) {
        content.score += boost[content.category];
      }

      // Visitor type adjustments
      if (visitorType === 'new' && content.category === 'info') content.score += 10;
      if (visitorType === 'returning' && content.title.includes('FAQ')) content.score -= 5;
      if (visitorType === 'active' && content.category !== 'info') content.score += 5;
    });

    return allContent
      .filter(c => c.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
  };

  return {
    history,
    segment,
    visitorType,
    interactions,
    trackPageView,
    trackInteraction,
    getRecommendations
  };
}